/**
 * Question Card Component
 * 
 * Minimal selection UI with staggered fade-in animation.
 * Supports multi-question flows with smooth transitions.
 */

import { memo, useState, useEffect, forwardRef } from 'react'
import { cn } from '../../lib/utils'

export interface QuestionOption {
  label: string
  value?: string
  description?: string
  icon?: string
}

export interface QuestionCardProps {
  question: string
  options: QuestionOption[]
  selectedOption?: string
  onSelect: (option: string) => void
  disabled?: boolean
  allowCustomInput?: boolean
  customText?: string
  onCustomTextChange?: (text: string) => void
  onCustomInputFocus?: () => void
  onCustomSubmit?: () => void
  autoSubmit?: boolean
  /** For multi-question flows: callback when selection is made */
  onSelectionMade?: () => void
  /** Whether this card is part of a multi-question set */
  isMultiQuestion?: boolean
  /** Index in multi-question set (for staggered animations) */
  questionIndex?: number
}

export const QuestionCard = memo(forwardRef<HTMLDivElement, QuestionCardProps>(function QuestionCard({
  question,
  options,
  selectedOption,
  onSelect,
  disabled = false,
  allowCustomInput = false,
  customText = '',
  onCustomTextChange,
  onCustomInputFocus,
  onCustomSubmit,
  onSelectionMade,
  isMultiQuestion = false,
  questionIndex = 0,
}, ref) {
  const [localSelected, setLocalSelected] = useState<string | undefined>(selectedOption)
  const hasSelection = !!localSelected || !!selectedOption

  useEffect(() => {
    setLocalSelected(selectedOption)
  }, [selectedOption])

  const handleSelect = (value: string) => {
    if (disabled || hasSelection) return
    setLocalSelected(value)
    onSelect(value)
    
    // In multi-question mode, notify parent to scroll to next question
    if (isMultiQuestion) {
      setTimeout(() => {
        onSelectionMade?.()
      }, 150)
    }
  }

  // Single option: inline text prompt
  if (options.length === 1) {
    const option = options[0]
    const optionLabel = typeof option === 'string' ? option : option.label
    const optionValue = typeof option === 'object' ? (option.value || optionLabel) : optionLabel

    return (
      <div className="my-3 text-base text-text-secondary animate-fade-in-down">
        {question}{' '}
        <button
          onClick={() => handleSelect(optionValue)}
          disabled={disabled}
          className="text-text-primary underline underline-offset-2 hover:text-primary transition-colors"
        >
          {optionLabel}
        </button>
      </div>
    )
  }

  // Multiple options: staggered fade-in animation
  return (
    <div 
      ref={ref}
      data-testid="question-card" 
      className="my-4 transition-all duration-300"
    >
      {/* Question header - always visible, uses chat font style */}
      <div
        className={cn('mb-3', !disabled && 'animate-fade-in-down')}
        style={!disabled ? { animationDelay: isMultiQuestion ? `${questionIndex * 100}ms` : '300ms' } : undefined}
      >
        <span data-testid="question-header" className="text-base text-text-secondary">
          {question}
        </span>
      </div>

      {/* Options - staggered fade in from top */}
      <div className="flex flex-col gap-1.5">
        {options.map((option, index) => {
          const optionLabel = typeof option === 'string' ? option : option.label
          const optionValue = typeof option === 'object' ? (option.value || optionLabel) : optionLabel
          const optionDescription = typeof option === 'object' ? option.description : undefined
          const isSelected = (localSelected || selectedOption) === optionValue
          const isOther = hasSelection && !isSelected

          // Hide non-selected options entirely when answered (disabled) to avoid gap spacing
          if (disabled && isOther) return null

          return (
            <button
              key={index}
              data-testid={`question-option-${optionValue}`}
              onClick={() => handleSelect(optionValue)}
              disabled={disabled || hasSelection}
              className={cn(
                'w-full px-3 py-2 text-left transition-all duration-300',
                'focus:outline-none rounded border',
                // Don't re-animate answered (disabled) cards — avoids opacity:0 initial state
                !disabled && 'animate-fade-in-down',
                disabled && 'cursor-not-allowed',
                isSelected && 'bg-surface-1 border-border',
                !disabled && isOther && 'opacity-0 h-0 py-0 my-0 overflow-hidden border-transparent',
                !isSelected && !isOther && 'border-border-subtle hover:border-border hover:bg-surface-1 cursor-pointer'
              )}
              style={!disabled ? {
                animationDelay: isMultiQuestion
                  ? `${(questionIndex * 100) + 100 + (index * 50)}ms`
                  : `${400 + (index * 75)}ms`,
                transitionProperty: isOther ? 'opacity, height, padding, margin' : 'all',
              } : undefined}
            >
              <div className="text-base text-text-primary font-medium">
                {optionLabel}
              </div>
              {optionDescription && !isOther && (
                <div className="text-sm text-text-muted leading-snug">
                  {optionDescription}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Custom input */}
      {allowCustomInput && (
        <div 
          className="mt-2 animate-fade-in-down"
          style={{ 
            animationDelay: `${400 + (options.length * 75)}ms`
          }}
        >
          <input
            type="text"
            value={customText}
            onChange={(e) => onCustomTextChange?.(e.target.value)}
            onFocus={onCustomInputFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customText.trim() && onCustomSubmit) {
                e.preventDefault()
                onCustomSubmit()
              }
            }}
            placeholder="Or type your own..."
            disabled={disabled}
            className={cn(
              'w-full px-3 py-2 bg-transparent text-base text-foreground rounded',
              'placeholder:text-text-muted transition-colors border',
              disabled
                ? 'opacity-50 cursor-not-allowed border-border-subtle'
                : 'border-border-subtle hover:border-border focus:border-border focus:outline-none'
            )}
          />
        </div>
      )}
    </div>
  )
}))
